import { Pagination, Typography, useTheme } from '@mui/material';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { trackWindowScroll } from 'react-lazy-load-image-component';
import Store from 'src/stores/RootStore';
import SparePartsList from './SparePartsList';
import Loading from 'src/components/loading/Loading';
import { TProductModel } from 'src/stores/types/ProductTypes';
import { CategoriesListBREKR, ItemPerPageDefault } from 'src/utils/constants';
import Button from 'src/components/button/Button';

interface SparePartsPageProps {
    path: string;
    store?: typeof Store.Type;
}

const SparePartsBrekr = inject('store')(
    observer(({ store }: SparePartsPageProps) => {
        const { palette } = useTheme();
        const [sparePartFilterText, setSparePartFilterText] = useState('');
        const [currentPage, setCurrentPage] = useState(1);
        const [categoryFilter, setCategoryFilter] = useState('All');

        useEffect(() => {
            store?.spareParts.loadAllSpareParts();
        }, []);

        const spareParts = useMemo(
            () => store!.spareParts.filteredList(sparePartFilterText, false),
            [store!.spareParts.list.size, sparePartFilterText],
        );

        const filteredByCategory = useMemo(() => {
            if (categoryFilter === 'All') {
                return spareParts;
            }
            return spareParts.filter((part: TProductModel) => part.category === categoryFilter);
        }, [categoryFilter, spareParts]);

        const paginatedProducts = useMemo(() => {
            const paginate = (array: TProductModel[], pageSize: number, pageNumber: number) => {
                return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
            };

            return paginate(filteredByCategory, ItemPerPageDefault, currentPage);
        }, [currentPage, filteredByCategory]);

        const handleTextFilterChange = (value: string) => {
            setSparePartFilterText(value);
            setCurrentPage(1);
            setCategoryFilter('All');
        };

        const handleCategoryChange = (category: string) => {
            setCategoryFilter(category);
            setCurrentPage(1);
            setSparePartFilterText('');
        };

        return (
            <>
                <div>
                    <Typography variant="h5">
                        <FormattedMessage id="menu.spare_parts" />
                    </Typography>

                    {store!.spareParts.loadingList ? (
                        <Loading small />
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                {Object.keys(CategoriesListBREKR).map(category => {
                                    return (
                                        <div key={category} style={{ display: 'flex', alignItems: 'center' }}>
                                            <Button
                                                color={
                                                    CategoriesListBREKR[category] === categoryFilter
                                                        ? 'secondary'
                                                        : 'primary'
                                                }
                                                onClick={() => handleCategoryChange(CategoriesListBREKR[category])}
                                                disabled={sparePartFilterText.length > 0}
                                            >
                                                <FormattedMessage id={`category.${category.toLowerCase()}`} />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                            <SparePartsList
                                spareParts={paginatedProducts}
                                showTextFilter
                                textFilter={sparePartFilterText}
                                onChangeTextFilter={handleTextFilterChange}
                                notFoundText={sparePartFilterText}
                            />
                        </>
                    )}
                </div>
                <div
                    style={{
                        textAlign: 'center',
                        marginTop: '1em',
                        position: 'absolute',
                        bottom: 0,
                        left: 240,
                        right: 0,
                    }}
                >
                    <Pagination
                        sx={{
                            background: palette.background.default,
                            padding: '0.5em 1em',
                            display: 'inline-block',
                            boxShadow: '0 0 15px rgba(0,0,0,.15)',
                        }}
                        size="small"
                        shape="rounded"
                        boundaryCount={2}
                        color="secondary"
                        count={Math.ceil(filteredByCategory.length / ItemPerPageDefault)}
                        page={currentPage}
                        onChange={(event, value) => {
                            document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
                            setCurrentPage(value);
                        }}
                    />
                </div>
            </>
        );
    }),
);

const pageWithTrackWindowScroll = trackWindowScroll(SparePartsBrekr);

export default pageWithTrackWindowScroll;
